import React, { createContext, useContext, useState, useEffect } from 'react';
import { themes, defaultTheme } from './themes';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Get theme from localStorage or use light as default
    const savedTheme = localStorage.getItem('theme');
    
    // Check if saved theme exists and is valid
    if (savedTheme && themes[savedTheme]) {
      return savedTheme;
    } else {
      return 'light';
    }
  });

  useEffect(() => {
    // Apply theme to document
    const root = document.documentElement;
    
    // Remove all existing theme attributes
    root.removeAttribute('data-theme');
    
    // Apply new theme if not default
    if (theme !== 'light') {
      root.setAttribute('data-theme', theme);
    }
    
    // Force a reflow to ensure CSS variables are applied
    root.style.display = 'none';
    root.offsetHeight; // Trigger reflow
    root.style.display = '';
    
    // Save to localStorage
    localStorage.setItem('theme', theme);
  }, [theme]);

  const changeTheme = (newTheme) => {
    if (themes[newTheme]) {
      setTheme(newTheme);
    } else {
      console.warn(`Theme "${newTheme}" not found`);
    }
  };

  const currentTheme = themes[theme];

  const value = {
    theme,
    currentTheme,
    changeTheme,
    availableThemes: Object.keys(themes),
    isDark: theme === 'dark',
    isLight: theme === 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Hook for getting theme-aware styles
export const useThemeColors = () => {
  const { currentTheme } = useTheme();
  
  return {
    // Primary colors
    primary: currentTheme.primary,
    primaryHover: currentTheme.primary[600],
    
    // Secondary colors
    secondary: currentTheme.secondary,
    
    // Semantic colors
    success: currentTheme.success,
    warning: currentTheme.warning,
    error: currentTheme.error,
    
    // Neutral colors
    neutral: currentTheme.neutral,
    
    // Text colors
    text: currentTheme.text,
    
    // Background colors
    background: currentTheme.background,
    surface: currentTheme.surface,
    card: currentTheme.card,
    
    // Border colors
    border: currentTheme.border,
    
    // Shadow colors
    shadow: currentTheme.shadow
  };
};
